if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.lastInteractionType = 'unknown';

        if (!this.elements.viewer || !this.elements.thumbnails) return;

        // IMPORTANT: actual mobile scroller is the inner slider list, not the slider-component wrapper
        this.viewerScroller =
          this.elements.viewer.querySelector('[id^="Slider-Gallery-"]') || this.elements.viewer;

        this.onSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.syncActiveMediaToVisibleSlide = this.syncActiveMediaToVisibleSlide.bind(this);

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged);
        this.elements.thumbnails.addEventListener('click', this.onThumbnailClick);

        this.bindMobileScrollSync();

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onThumbnailClick(event) {
        const button = event.target.closest('button');
        if (!button) return;

        const thumbnail = button.closest('[data-target]');
        if (!thumbnail) return;

        event.preventDefault();
        this.lastInteractionType = 'thumbnail';
        this.setActiveMedia(thumbnail.dataset.target, false);
      }

      onSlideChanged(event) {
        const mediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        const activeMedia =
          this.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.querySelector('.product__media-item.is-active') ||
          this.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });

        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(activeMedia);
        this.notifyMediaChange(mediaId, activeMedia, thumbnail);
      }

      setActiveMedia(mediaId, prepend = false) {
        if (!mediaId || !this.viewerScroller) return;

        if (!this.lastInteractionType || this.lastInteractionType === 'unknown') {
          this.lastInteractionType = 'programmatic';
        }

        const activeMedia =
          this.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        // No mobile DOM reordering
        if (prepend && this.mql.matches) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches && this.viewerScroller) {
            const targetLeft = activeMedia.offsetLeft;
            const currentLeft = this.viewerScroller.scrollLeft;

            if (Math.abs(currentLeft - targetLeft) > 2) {
              this.viewerScroller.scrollTo({
                left: targetLeft,
                behavior: 'auto',
              });
            }
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.notifyMediaChange(mediaId, activeMedia, null);
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        this.notifyMediaChange(mediaId, activeMedia, activeThumbnail);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', 'true');

        if (!this.elements.thumbnails.slider || this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
          behavior: this.mql.matches ? 'smooth' : 'auto',
        });
      }

      getVisibleMedia() {
        if (!this.viewerScroller) return null;

        const mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
        if (!mediaItems.length) return null;

        const center = this.viewerScroller.scrollLeft + this.viewerScroller.clientWidth / 2;

        let best = null;
        let bestDistance = Infinity;

        mediaItems.forEach((item) => {
          const itemCenter = item.offsetLeft + item.clientWidth / 2;
          const distance = Math.abs(center - itemCenter);

          if (distance < bestDistance) {
            bestDistance = distance;
            best = item;
          }
        });

        return best;
      }

      syncActiveMediaToVisibleSlide() {
        if (this.mql.matches || !this.viewerScroller) return;

        const visibleMedia = this.getVisibleMedia();
        if (!visibleMedia) return;

        const mediaId = visibleMedia.dataset.mediaId;
        if (!mediaId) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === visibleMedia);
        });

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);

        this.lastInteractionType = 'swipe';
        this.notifyMediaChange(mediaId, visibleMedia, thumbnail);
      }

      bindMobileScrollSync() {
        if (!this.viewerScroller) return;
        if (this.viewerScroller.dataset.mediaGalleryScrollBound === 'true') return;

        this.viewerScroller.dataset.mediaGalleryScrollBound = 'true';

        let scrollTimer = null;

        if ('onscrollend' in window) {
          this.viewerScroller.addEventListener(
            'scrollend',
            this.syncActiveMediaToVisibleSlide,
            { passive: true }
          );
        } else {
          this.viewerScroller.addEventListener(
            'scroll',
            () => {
              clearTimeout(scrollTimer);
              scrollTimer = setTimeout(this.syncActiveMediaToVisibleSlide, 140);
            },
            { passive: true }
          );
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      notifyMediaChange(mediaId, activeMedia, activeThumbnail) {
        document.dispatchEvent(
          new CustomEvent('media:changed', {
            detail: {
              mediaId,
              activeMedia,
              activeThumbnail,
              gallery: this,
              interactionType: this.lastInteractionType || 'unknown',
            },
          })
        );

        this.lastInteractionType = 'unknown';
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}